<template>
  <div class="report-send-report">
    <AppDialog
      ref="sendReportDialog"
      append-to-body
      class="send-report-dialog"
      :title="dialogTitle"
      :show="showDialog"
      :loading="loading"
      @close="onCloseDialog"
    >
      <div class="row">
        <div class="col-sm-12">
          <template v-if="blacklisted">
            <AppBlacklistedMessage />
          </template>
          <template v-else>
            <div
              class="form-group"
              :class="{ 'has-error': hasErrorsFor('recipients') }"
            >
              <div>
                <label
                  class="control-label"
                  for="sendReport_recipients"
                >
                  Email addresses*
                </label>
              </div>
              <div class="hint hint-small">Separate email addresses with commas</div>
              <AppInput
                id="sendReport_recipients"
                v-model="reportEmail.recipients"
              />
              <div
                v-for="error in errorsFor('recipients')"
                :key="error"
                class="help-block"
              >
                {{ error }}
              </div>
            </div>
            <div class="form-group">
              <label class="control-label">Subject</label>
              <p>{{ subject }}</p>
            </div>
            <div
              class="form-group"
              :class="{ 'has-error': hasErrorsFor('message') }"
            >
              <label
                class="control-label"
                for="sendReport_message"
              >
                Message
              </label>
              <textarea
                id="sendReport_message"
                v-model="reportEmail.message"
                class="form-control"
                :readonly="!customMessageAllowed"
                :maxlength="messageMaxLength"
              />
              <div
                v-for="(error, index) in errorsFor('message')"
                :key="index"
                class="help-block"
              >
                {{ error }}
              </div>
            </div>
            <div
              v-if="!customMessageAllowed"
              class="alert alert-warning"
            >
              <i class="fa fa-warning" />
              Custom messages are only enabled on paid plans
            </div>
            <div class="control-group">
              <label
                class="control-label"
                for="sendReport_file_format"
              >
                File type
              </label>
              <AppSelect
                id="sendReport_file_format"
                v-model="reportEmail.file_format"
                :options="formatOptions"
                :select2-options="{ width: '100%' }"
                :disabled="formatSelectionDisabled"
              />
            </div>
          </template>
        </div>
      </div>
      <template #footer>
        <button
          id="sendReport_close_button"
          class="btn btn-default"
          :disabled="sending || sendingPreview"
          @click="onCloseDialog"
        >
          {{ cancelButtonLabel }}
        </button>
        <template v-if="!blacklisted">
          <button
            id="sendReport_preview_button"
            class="btn btn-default"
            :disabled="disableButtons"
            @click="onPreview"
          >
            {{ previewButtonLabel }}
          </button>
          <button
            id="sendReport_save_button"
            class="btn btn-primary"
            :disabled="isInvalid || disableButtons"
            @click="onSend"
          >
            {{ sendButtonLabel }}
          </button>
        </template>
      </template>
    </AppDialog>
    <a
      v-tooltip="disabled"
      href="#"
      class="btn btn-naked send-report-link"
      :disabled="disabled ? 'disabled' : null"
      :title="disabledTooltip"
      data-placement="bottom"
      @click.prevent="onShowDialog"
    >
      <i class="hi hi-paper-plane hi-16" />
      Send
    </a>
  </div>
</template>

<script>
import { clone } from 'lodash-es';
import { mapConstants, SEND_REPORT_FORMATS } from '~/app_constants';
import { mapState, mapActions } from 'vuex';

const ERROR_MESSAGE = 'Oops, something went wrong';
const RATE_LIMITED_ERROR_MESSAGE = 'You are sending too many messages. Wait for a while before sending again.';

export default {
  name: 'ReportSendReport',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    formatOptionIds: {
      type: Array,
      default() {
        return SEND_REPORT_FORMATS.map(({ id }) => id);
      },
    },
  },
  data() {
    return {
      loading: false,
      showDialog: false,
      sending: false,
      sendingPreview: false,
      errors: null,
      blacklisted: false,
      customMessageAllowed: true,
      messageMaxLength: 200,
      subject: null,
      reportEmail: {},
    };
  },
  computed: {
    ...mapConstants(['noDataToSendTooltip']),
    ...mapState({
      currentUserOrganizationId: 'current_user_organization_id',
    }),
    cancelButtonLabel() {
      return this.blacklisted ? 'Close' : 'Cancel';
    },
    defaultReportEmail() {
      return {
        recipients: null,
        message: null,
        file_format: this.formatOptions[0] && this.formatOptions[0].id,
      };
    },
    dialogTitle() {
      return this.blacklisted ? 'You have been blocked' : 'Send report';
    },
    disableButtons() {
      return this.sendingPreview || this.sending;
    },
    disabledTooltip() {
      if (this.disabled) {
        return this.noDataToSendTooltip;
      }

      return null;
    },
    formatOptions() {
      return SEND_REPORT_FORMATS.filter(({ id }) => this.formatOptionIds.includes(id));
    },
    formatSelectionDisabled() {
      return this.formatOptions.length < 2;
    },
    isValid() {
      const recipients = this.reportEmail.recipients;

      return recipients && recipients.length > 3;
    },
    isInvalid() {
      return !this.isValid;
    },
    previewButtonLabel() {
      return this.sendingPreview ? 'Sending...' : 'Send preview';
    },
    sendButtonLabel() {
      return this.sending ? 'Sending...' : 'Send';
    },
  },
  beforeMount() {
    this.reportEmail = clone(this.defaultReportEmail);
  },
  methods: {
    ...mapActions(['analyticsTrackReport']),
    sendReportCheck() {
      this.loading = true;

      const success = (response) => {
        if (response.success) {
          this.reportEmail.message = response.message;
          this.subject = response.subject;
          this.customMessageAllowed = response.custom_messages_allowed;
          this.messageMaxLength = response.message_max_length;
        } else {
          this.handleSendError(response);
        }
      };
      const error = () => {
        this.$appMessage(ERROR_MESSAGE);
      };
      const fin = () => {
        this.loading = false;
      };

      this.$store.dispatch('sendReportCheck', this.reportEmail).then(success, error).then(fin);
    },
    onShowDialog() {
      if (this.disabled) {
        return;
      }

      this.showDialog = true;

      if (!this.blacklisted) {
        this.sendReportCheck();
      }

      this.analyticsTrackReport({ event: 'Report send opened' });
    },
    onCloseDialog() {
      this.showDialog = false;
      this.reset();
    },
    onPreview() {
      this.sendingPreview = true;

      const success = (response) => {
        if (response.success) {
          this.$appMessage('Preview sent');
        } else {
          this.handleSendError(response);
        }
      };
      const error = () => {
        this.$appMessage(ERROR_MESSAGE);
      };
      const fin = () => {
        this.sendingPreview = false;
      };

      this.$store.dispatch('sendReportPreview', clone(this.reportEmail)).then(success, error).then(fin);
    },
    onSend() {
      this.sending = true;

      const success = (response) => {
        if (response.success) {
          this.$appMessage('Report sent');

          if (response.show_scheduled_report_promotion_banner_dialog) {
            this.$emit('open-dialog', 'scheduledReportBannerDialog');
          }

          this.onCloseDialog();

          this.analyticsTrackReport({
            event: 'Report sent',
            dateProps: true,
            groupProps: true,
            filtersProps: true,
            colsProps: true,
            props: {
              export_value: this.reportEmail.file_format,
            },
          });
        } else {
          this.handleSendError(response);
        }
      };
      const error = () => {
        this.$appMessage(ERROR_MESSAGE);
      };
      const fin = () => {
        this.sending = false;
      };

      this.$store.dispatch('sendReport', clone(this.reportEmail)).then(success, error).then(fin);
    },
    errorsFor(key) {
      if (this.errors) {
        return this.errors[key];
      }
    },
    hasErrorsFor(key) {
      const errorList = this.errorsFor(key);

      return Array.isArray(errorList) && errorList.length > 0;
    },
    handleSendError(response) {
      if (response.message) {
        this.$appMessage(response.message);
      } else if (response.rate_limited) {
        this.$appMessage(RATE_LIMITED_ERROR_MESSAGE);
        this.onCloseDialog();
      } else if (response.blacklisted) {
        this.blacklisted = true;
      } else {
        this.$appMessage(ERROR_MESSAGE);
      }

      this.errors = response.errors;
    },
    reset() {
      this.subject = null;
      this.errors = null;
      this.reportEmail = clone(this.defaultReportEmail);
    },
  },
};
</script>
